import { useCallback } from "react";
import type { PanInfo } from "framer-motion";
import { useDragControls } from "framer-motion";

import {
  SHEET_DRAG_DISMISS_OFFSET_PX,
  SHEET_DRAG_DISMISS_VELOCITY,
  SHEET_DRAG_ELASTIC_BOTTOM,
  SHEET_DRAG_ELASTIC_TOP,
} from "./tokens";

// Trace logging for sheet drag-to-dismiss debugging. Default ON in dev.
// Silence with: `window.__pontimedSheetDebug = false` in DevTools.
// Tagged `[sheet]` so it's easy to filter alongside `[swipe]`.
const isProd = (() => {
  const proc = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process;
  return proc?.env?.NODE_ENV === "production";
})();
// Exported so TS's noUnusedLocals doesn't complain — the helper is kept
// for future debug toggling but the 5 call sites below are commented out
// (see commit 97d0a410). External consumers can also re-enable per-call-
// site logging by importing this directly. Plain eslint-disable doesn't
// silence TS noUnusedLocals; export does.
export const sheetLog = (...args: unknown[]) => {
  if (typeof window === "undefined") return;
  if (isProd) return;
  const flag = (window as { __pontimedSheetDebug?: boolean }).__pontimedSheetDebug;
  if (flag === false) return;
  console.log("[sheet]", ...args);
};

interface UseDragToDismissOptions {
  /** Called when the user drags past the threshold and releases. */
  onDismiss: () => void;
  /** Disable drag entirely (e.g. desktop dialog variant). Default: false. */
  disabled?: boolean;
}

interface UseDragToDismissResult {
  dragControls: ReturnType<typeof useDragControls>;
  /** Spread on the root <motion.div>. */
  motionDragProps: {
    drag: "y";
    dragControls: ReturnType<typeof useDragControls>;
    dragListener: false;
    dragConstraints: { top: 0; bottom: 0 };
    dragElastic: { top: number; bottom: number };
    dragMomentum: false;
    onDragEnd: (event: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => void;
  } | null;
  /** Spread on the drag handle area to start drag from a specific zone. */
  handleProps: {
    onPointerDown: (event: React.PointerEvent) => void;
  } | null;
  /**
   * Spread on the scrollable body wrapper to enable iOS-style "scroll-then-
   * drag" handoff — pull-down gesture starts a scroll inside the body; once
   * scrollTop hits 0 and the user keeps pulling down, the gesture hands off
   * to the sheet drag. Matches Apple Maps / Music / Photos sheet behaviour.
   *
   * Skipped if pointerdown lands on an interactive element (button, link,
   * input, textarea, [role=button|switch|...] etc.) so taps/clicks/typing
   * never trigger drag.
   */
  bodyHandoffProps: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  } | null;
}

/**
 * Wires framer-motion drag controls into a sheet so:
 *   - Body content stays scrollable (drag listener disabled by default)
 *   - Drag is initiated only by handle pointerdown via `dragControls.start`
 *   - Release past `SHEET_DRAG_DISMISS_OFFSET_PX` or velocity threshold dismisses
 *
 * Returns `motionDragProps = null` and `handleProps = null` when disabled, so
 * the same shell renders without drag at desktop breakpoint.
 */
export function useDragToDismiss({
  onDismiss,
  disabled = false,
}: UseDragToDismissOptions): UseDragToDismissResult {
  const dragControls = useDragControls();

  const onDragEnd = useCallback(
    (_event: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
      const distanceCommit = info.offset.y > SHEET_DRAG_DISMISS_OFFSET_PX;
      const velocityCommit = info.velocity.y > SHEET_DRAG_DISMISS_VELOCITY;
      // sheetLog(
      //   `dragEnd — offset.y=${info.offset.y.toFixed(0)}, velocity.y=${info.velocity.y.toFixed(0)}, ` +
      //     `distanceCommit=${distanceCommit} (>${SHEET_DRAG_DISMISS_OFFSET_PX}), ` +
      //     `velocityCommit=${velocityCommit} (>${SHEET_DRAG_DISMISS_VELOCITY}) → ` +
      //     `${distanceCommit || velocityCommit ? "DISMISS" : "snap back"}`,
      // );
      if (distanceCommit || velocityCommit) {
        onDismiss();
      }
    },
    [onDismiss],
  );

  const startDrag = useCallback(
    (event: React.PointerEvent) => {
      // sheetLog(
      //   `handle pointerdown — type=${event.pointerType}, x=${event.clientX.toFixed(0)}, y=${event.clientY.toFixed(0)} → starting framer drag`,
      // );
      dragControls.start(event);
    },
    [dragControls],
  );

  // Body-level handoff: lets the user pull-down from anywhere on the sheet,
  // mimicking iOS Maps / Music / Photos. Listens at pointerdown, watches
  // pointermove, and only hands off to framer drag when:
  //   1. pointerdown didn't land on an interactive element (button, input, etc.)
  //   2. scroll ancestor (the body's nearest overflow-y) is at scrollTop=0
  //   3. movement is downward and dominantly vertical (dy > 6, dy > dx)
  // Otherwise lets the body scroll natively.
  const startBodyHandoff = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      // Skip drags initiated on interactive content. Drag-to-dismiss must
      // not steal click/tap/typing gestures from form fields, buttons,
      // links, switches, etc. inside the sheet body.
      const target = event.target as HTMLElement | null;
      if (
        target?.closest?.(
          'button, [role="button"], a, input, textarea, select, label, ' +
            '[role="switch"], [role="checkbox"], [role="radio"], [role="menuitem"], ' +
            '[role="slider"], [role="tab"], [contenteditable="true"], [data-no-swipe]',
        )
      ) {
        return;
      }

      // Find the nearest scrollable ancestor (the body container). If found,
      // we gate handoff on scrollTop=0; otherwise (no scroll inside sheet)
      // any pull-down hands off immediately.
      let scrollEl: HTMLElement | null = null;
      let p: HTMLElement | null = event.currentTarget as HTMLElement;
      while (p) {
        const cs = getComputedStyle(p);
        if (cs.overflowY === "auto" || cs.overflowY === "scroll") {
          scrollEl = p;
          break;
        }
        p = p.parentElement;
      }

      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      const nativeEvent = event.nativeEvent;

      const onMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        const dx = Math.abs(moveEvent.clientX - startX);
        const dy = moveEvent.clientY - startY;
        // Only pull-down counts (dy > 0). Dominantly vertical (|dy| > dx).
        // Threshold 6 mirrors the carousel's pointer gating.
        if (dy >= 6 && dy > dx) {
          // Body must be at the top of its own scroll for handoff to engage.
          // If user is scrolled mid-content, let the native scroll happen.
          if (scrollEl && scrollEl.scrollTop > 0) {
            // sheetLog(`body handoff SKIPPED — scrollTop=${scrollEl.scrollTop}, native scroll wins`);
            cleanup();
            return;
          }
          // sheetLog(`body handoff → framer drag, dy=${dy.toFixed(0)}, dx=${dx.toFixed(0)}`);
          cleanup();
          dragControls.start(nativeEvent);
        } else if (dy <= -6 || dx >= 6) {
          // Upward or sideways gesture — release. Body owns it.
          cleanup();
        }
      };

      const cleanup = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", cleanup);
        window.removeEventListener("pointercancel", cleanup);
      };

      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerup", cleanup);
      window.addEventListener("pointercancel", cleanup);
    },
    [dragControls],
  );

  if (disabled) {
    // sheetLog(`drag DISABLED (desktop dialog variant)`);
    return { dragControls, motionDragProps: null, handleProps: null, bodyHandoffProps: null };
  }

  return {
    dragControls,
    motionDragProps: {
      drag: "y",
      dragControls,
      dragListener: false,
      dragConstraints: { top: 0, bottom: 0 },
      dragElastic: { top: SHEET_DRAG_ELASTIC_TOP, bottom: SHEET_DRAG_ELASTIC_BOTTOM },
      dragMomentum: false,
      onDragEnd,
    },
    handleProps: {
      onPointerDown: startDrag,
    },
    bodyHandoffProps: {
      onPointerDown: startBodyHandoff,
    },
  };
}
